import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPlatformAdminEmail, PLATFORM_ADMIN_EMAIL } from '../../constants/platformAdmin';
import { fetchUserStatDetail } from '../../services/adminUserStatsService';
import {
  grantPlatformManager,
  listPlatformManagers,
  revokePlatformManager,
  type PlatformManagerRow,
} from '../../services/platformManagerService';
import type { PlatformWorkspaceAccountRow } from '../../types/adminUserStats';
import { formatLastAccess } from '../../utils/formatLastAccess';

function formatGrantedAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`;
}

function StaffAccountBlock({
  email,
  displayName,
  userId,
  role,
  meta,
}: {
  email: string;
  displayName: string;
  userId: string;
  role: 'admin' | 'manager';
  meta?: string;
}) {
  return (
    <div className="cp-admin-users-pass-user-block">
      <div className="cp-admin-users-pass-user-head-row">
        <div className="cp-admin-users-pass-user-head-left">
          <div className="cp-admin-users-pass-user-team-head">{displayName}</div>
          <span
            className={
              role === 'admin'
                ? 'cp-admin-users-pass-user-admin-badge'
                : 'cp-admin-users-pass-user-manager-badge'
            }
            title={role === 'admin' ? '플랫폼 관리자' : '플랫폼 매니저'}
          >
            {role === 'admin' ? '관리자' : '매니저'}
          </span>
        </div>
      </div>
      <div className="cp-admin-users-pass-user-email-row">
        <span className="cp-admin-users-pass-user-k">계정</span>
        <span className="cp-admin-users-pass-user-email-lg">{email}</span>
      </div>
      <div className="cp-admin-users-pass-user-name-row">
        <span className="cp-admin-users-pass-user-k">이름</span>
        <span className="cp-admin-users-pass-user-name-md">{displayName}</span>
      </div>
      <div className="cp-admin-users-pass-user-uid-row">
        <span className="cp-admin-users-pass-user-k">UID</span>
        <span className="cp-admin-users-pass-user-uid-sm" title="사용자 UID">
          {userId}
        </span>
      </div>
      {meta ? (
        <div className="cp-admin-users-pass-modal-li-meta cp-admin-users-pass-modal-li-meta--after-user">
          {meta}
        </div>
      ) : null}
    </div>
  );
}

export interface AdminPlatformStaffModalsProps {
  open: boolean;
  workspaceId: string;
  onClose: () => void;
  onManagersChanged?: (managers: PlatformManagerRow[]) => void;
}

const AdminPlatformStaffModals: React.FC<AdminPlatformStaffModalsProps> = ({
  open,
  workspaceId,
  onClose,
  onManagersChanged,
}) => {
  const [managers, setManagers] = useState<PlatformManagerRow[]>([]);
  const [adminAccount, setAdminAccount] = useState<PlatformWorkspaceAccountRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [grantOpen, setGrantOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allAccounts, setAllAccounts] = useState<PlatformWorkspaceAccountRow[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PlatformManagerRow | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const onManagersChangedRef = useRef(onManagersChanged);
  onManagersChangedRef.current = onManagersChanged;

  useEffect(() => {
    if (!open) {
      setGrantOpen(false);
      setSearchQuery('');
      setSelectedUserId(null);
      setGrantError(null);
      setRevokeTarget(null);
      setRevokeError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [mgrs, detail] = await Promise.all([
          listPlatformManagers(),
          fetchUserStatDetail(workspaceId, 'allAccounts'),
        ]);
        if (cancelled) return;
        setManagers(mgrs);
        onManagersChangedRef.current?.(mgrs);
        if (detail.kind === 'allAccounts') {
          const admin = detail.rows.find((r) => isPlatformAdminEmail(r.email)) ?? null;
          setAdminAccount(admin);
          setAllAccounts(detail.rows);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
        setManagers([]);
        setAdminAccount(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const reloadStaff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mgrs, detail] = await Promise.all([
        listPlatformManagers(),
        fetchUserStatDetail(workspaceId, 'allAccounts'),
      ]);
      setManagers(mgrs);
      onManagersChangedRef.current?.(mgrs);
      if (detail.kind === 'allAccounts') {
        const admin = detail.rows.find((r) => isPlatformAdminEmail(r.email)) ?? null;
        setAdminAccount(admin);
        setAllAccounts(detail.rows);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
      setManagers([]);
      setAdminAccount(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const managerIdSet = useMemo(() => new Set(managers.map((m) => m.user_id)), [managers]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allAccounts.filter((r) => {
      if (isPlatformAdminEmail(r.email)) return false;
      if (managerIdSet.has(r.user_id)) return false;
      const email = r.email.toLowerCase();
      const name = r.display_name.toLowerCase();
      return email.includes(q) || name.includes(q);
    }).slice(0, 40);
  }, [allAccounts, managerIdSet, searchQuery]);

  const openGrantModal = useCallback(() => {
    setGrantOpen(true);
    setSearchQuery('');
    setSelectedUserId(null);
    setGrantError(null);
    if (allAccounts.length === 0) {
      setAccountsLoading(true);
      void fetchUserStatDetail(workspaceId, 'allAccounts')
        .then((detail) => {
          if (detail.kind === 'allAccounts') setAllAccounts(detail.rows);
        })
        .catch((e) => {
          setGrantError(e instanceof Error ? e.message : '계정 목록을 불러오지 못했습니다.');
        })
        .finally(() => setAccountsLoading(false));
    }
  }, [allAccounts.length, workspaceId]);

  const onGrant = useCallback(async () => {
    if (!selectedUserId) return;
    setBusy(true);
    setGrantError(null);
    try {
      await grantPlatformManager(selectedUserId);
      setGrantOpen(false);
      setSelectedUserId(null);
      setSearchQuery('');
      await reloadStaff();
    } catch (e) {
      setGrantError(e instanceof Error ? e.message : '권한 부여에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  }, [selectedUserId, reloadStaff]);

  const openRevokeModal = useCallback((row: PlatformManagerRow) => {
    setRevokeTarget(row);
    setRevokeError(null);
  }, []);

  const closeRevokeModal = useCallback(() => {
    if (busy) return;
    setRevokeTarget(null);
    setRevokeError(null);
  }, [busy]);

  const confirmRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    setBusy(true);
    setRevokeError(null);
    setError(null);
    try {
      await revokePlatformManager(revokeTarget.user_id);
      setRevokeTarget(null);
      await reloadStaff();
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : '권한 회수에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  }, [revokeTarget, reloadStaff]);

  if (!open) return null;

  return (
    <>
      <div
        className="cp-modal-backdrop"
        role="presentation"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (revokeTarget) closeRevokeModal();
            else if (grantOpen) setGrantOpen(false);
            else onClose();
          }
        }}
      >
        <div
          className="cp-modal cp-admin-users-pass-detail-modal cp-admin-users-pass-detail-modal--all-accounts"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-admin-platform-staff-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cp-admin-platform-staff-modal-head">
            <h3 id="cp-admin-platform-staff-title">관리자 · 매니저</h3>
            <button
              type="button"
              className="cp-btn-secondary cp-admin-platform-staff-grant-open"
              onClick={openGrantModal}
              disabled={busy || loading}
            >
              매니저 권한 부여
            </button>
          </div>
          <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
            {loading ? (
              <p className="cp-dashboard-placeholder" role="status">
                불러오는 중…
              </p>
            ) : error ? (
              <p className="cp-dashboard-placeholder" role="alert">
                {error}
              </p>
            ) : (
              <div className="cp-admin-users-pass-all-accounts-scroll">
                <ul className="cp-admin-users-pass-modal-list">
                  {adminAccount ? (
                    <li className="cp-admin-users-pass-modal-li">
                      <StaffAccountBlock
                        userId={adminAccount.user_id}
                        email={adminAccount.email}
                        displayName={adminAccount.display_name}
                        role="admin"
                        meta={`생성 ${formatGrantedAt(adminAccount.workspace_created_at)} · 마지막 접속 ${formatLastAccess(adminAccount.last_sign_in_at, adminAccount.last_active_on)}`}
                      />
                    </li>
                  ) : (
                    <li className="cp-admin-users-pass-modal-li">
                      <StaffAccountBlock
                        userId="—"
                        email={PLATFORM_ADMIN_EMAIL}
                        displayName="플랫폼 관리자"
                        role="admin"
                      />
                    </li>
                  )}
                  {managers.map((m) => (
                    <li key={m.user_id} className="cp-admin-users-pass-modal-li">
                      <button
                        type="button"
                        className={
                          'cp-admin-platform-staff-manager-hit' +
                          (revokeTarget?.user_id === m.user_id ? ' is-selected' : '')
                        }
                        disabled={busy}
                        title="클릭하여 매니저 권한 회수"
                        onClick={() => openRevokeModal(m)}
                      >
                        <StaffAccountBlock
                          userId={m.user_id}
                          email={m.email}
                          displayName={m.display_name}
                          role="manager"
                          meta={`부여 ${formatGrantedAt(m.granted_at)} · 클릭하여 권한 회수`}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
                {managers.length === 0 ? (
                  <p className="cp-admin-users-pass-modal-empty cp-admin-platform-staff-empty-hint">
                    등록된 매니저가 없습니다. 우측 상단에서 권한을 부여할 수 있습니다.
                  </p>
                ) : null}
              </div>
            )}
          </div>
          <div className="cp-modal-footer">
            <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={busy}>
              닫기
            </button>
          </div>
        </div>
      </div>

      {revokeTarget ? (
        <div
          className="cp-modal-backdrop cp-admin-platform-staff-grant-backdrop"
          role="presentation"
          onClick={closeRevokeModal}
        >
          <div
            className="cp-modal cp-admin-platform-staff-revoke-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-platform-staff-revoke-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-platform-staff-revoke-title">매니저 권한 회수</h3>
            <div className="cp-modal-body">
              <p className="cp-admin-platform-staff-revoke-desc">
                <strong>{revokeTarget.display_name}</strong> ({revokeTarget.email}) 계정의 매니저 권한을
                회수할까요?
              </p>
              <p className="cp-admin-platform-staff-revoke-hint">
                회수 후에는 학습내용·평가계획·범교과 관리 메뉴에 접근할 수 없습니다.
              </p>
              {revokeError ? (
                <p className="cp-dashboard-placeholder" role="alert">
                  {revokeError}
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer cp-admin-platform-staff-grant-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={closeRevokeModal}
                disabled={busy}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void confirmRevoke()}
                disabled={busy}
              >
                {busy ? '처리 중…' : '권한 회수하기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {grantOpen ? (
        <div
          className="cp-modal-backdrop cp-admin-platform-staff-grant-backdrop"
          role="presentation"
          onClick={() => !busy && setGrantOpen(false)}
        >
          <div
            className="cp-modal cp-admin-users-pass-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-platform-staff-grant-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-platform-staff-grant-title">매니저 권한 부여</h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              <label className="cp-admin-platform-staff-search-label" htmlFor="cp-admin-platform-staff-search">
                계정 검색
              </label>
              <input
                id="cp-admin-platform-staff-search"
                className="cp-admin-platform-staff-search-input"
                type="search"
                placeholder="이메일 또는 이름"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedUserId(null);
                }}
                autoComplete="off"
                disabled={busy || accountsLoading}
              />
              {accountsLoading ? (
                <p className="cp-dashboard-placeholder" role="status">
                  계정 목록 불러오는 중…
                </p>
              ) : null}
              {grantError ? (
                <p className="cp-dashboard-placeholder" role="alert">
                  {grantError}
                </p>
              ) : null}
              <div className="cp-admin-platform-staff-search-results">
                {!accountsLoading && searchQuery.trim() && searchResults.length === 0 ? (
                  <p className="cp-admin-users-pass-modal-empty">일치하는 계정이 없습니다.</p>
                ) : null}
                <ul className="cp-admin-users-pass-modal-list">
                  {searchResults.map((r) => {
                    const selected = selectedUserId === r.user_id;
                    return (
                      <li key={r.user_id} className="cp-admin-users-pass-modal-li">
                        <button
                          type="button"
                          className={
                            'cp-admin-platform-staff-manager-hit' + (selected ? ' is-selected' : '')
                          }
                          disabled={busy}
                          onClick={() => setSelectedUserId(r.user_id)}
                        >
                          <div className="cp-admin-users-pass-user-block">
                            <div className="cp-admin-users-pass-user-email-row">
                              <span className="cp-admin-users-pass-user-k">계정</span>
                              <span className="cp-admin-users-pass-user-email-lg">{r.email}</span>
                            </div>
                            <div className="cp-admin-users-pass-user-name-row">
                              <span className="cp-admin-users-pass-user-k">이름</span>
                              <span className="cp-admin-users-pass-user-name-md">{r.display_name}</span>
                            </div>
                            <div className="cp-admin-users-pass-user-uid-row">
                              <span className="cp-admin-users-pass-user-k">UID</span>
                              <span className="cp-admin-users-pass-user-uid-sm">{r.user_id}</span>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <div className="cp-modal-footer cp-admin-platform-staff-grant-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setGrantOpen(false)}
                disabled={busy}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void onGrant()}
                disabled={busy || !selectedUserId}
              >
                매니저 권한 부여하기
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AdminPlatformStaffModals;
